import React from 'react';
import { LegalDocument as Document } from './types';

function inline(text: string) {
  return text
    .split('**')
    .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

/** Plain legal text: no raw HTML, remote images, scripts, or auto-created links. */
export function LegalDocument({
  document,
  draft = false,
}: {
  document: Document;
  draft?: boolean;
}) {
  return (
    <article className="mx-auto max-w-4xl px-5 py-8 text-gray-900 leading-7">
      {draft && (
        <p
          role="note"
          className="mb-6 rounded border border-amber-400 bg-amber-50 p-4 font-semibold"
        >
          Review draft · {document.version} · Not effective or available for
          acceptance. Publication requires approval and resolution of the
          recorded blockers.
        </p>
      )}
      {document.content.split(/\n\n+/).map((block, i) => {
        if (block.startsWith('| ')) {
          const rows = block.split('\n').map((row) =>
            row
              .split('|')
              .slice(1, -1)
              .map((cell) => cell.trim()),
          );
          return (
            <div
              key={i}
              className="my-6 overflow-x-auto"
              tabIndex={0}
              role="region"
              aria-label="Policy information table"
            >
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr>
                    {rows[0].map((cell, c) => (
                      <th className="border p-3 align-top" scope="col" key={c}>
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(2).map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) =>
                        c === 0 ? (
                          <th
                            scope="row"
                            className="border p-3 align-top font-medium"
                            key={c}
                          >
                            {cell}
                          </th>
                        ) : (
                          <td className="border p-3 align-top" key={c}>
                            {cell}
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.startsWith('- '))
          return (
            <ul key={i} className="mb-4 list-disc space-y-2 pl-6">
              {block.split('\n').map((line, n) => (
                <li key={n}>{inline(line.replace(/^- /, ''))}</li>
              ))}
            </ul>
          );
        if (block.startsWith('### '))
          return (
            <h3 key={i} className="mt-6 mb-2 text-lg font-semibold">
              {block.slice(4)}
            </h3>
          );
        if (block.startsWith('## '))
          return (
            <h2 key={i} className="mt-8 mb-3 text-xl font-semibold">
              {block.slice(3)}
            </h2>
          );
        if (block.startsWith('# '))
          return (
            <h1 key={i} className="mb-6 text-3xl font-semibold">
              {block.slice(2)}
            </h1>
          );
        return (
          <p key={i} className="mb-4 whitespace-pre-line break-words">
            {inline(block)}
          </p>
        );
      })}
      <p className="mt-8 break-all text-xs text-gray-500">
        Content SHA-256: {document.sha256}
      </p>
    </article>
  );
}
